import { z } from 'zod';

const paginationMetaSchema = z.object({
  total: z.number(),
  page: z.number(),
  limit: z.number(),
  totalPages: z.number(),
});

const pcmpProjectBaseShape = {
  id: z.coerce.number(),
  displayName: z.string(),
  name: z.string(),
  code: z.string().nullable(),
  companyId: z.coerce.number(),
  companyName: z.string().nullable(),
  bookEditionNo: z.number().nullable(),
  bookEditionName: z.string().nullable(),
  activeFrom: z.string().nullable(),
  activeTo: z.string().nullable(),
  slNo: z.number().nullable(),
  isActive: z.boolean(),
  isWebVisible: z.boolean(),
  isMobileVisible: z.boolean(),
  submissionCount: z.number(),
  createdAt: z.coerce.date(),
};

export const pcmpProjectListItemSchema = z.object({
  ...pcmpProjectBaseShape,
  categoryCount: z.number(),
  priceCount: z.number(),
});

export type PcmpProjectListItem = z.infer<typeof pcmpProjectListItemSchema>;

export const paginatedPcmpProjectsSchema = z.object({
  data: z.array(pcmpProjectListItemSchema),
  meta: paginationMetaSchema,
});

export type PaginatedPcmpProjects = z.infer<typeof paginatedPcmpProjectsSchema>;

export interface PcmpProjectListParams {
  search?: string | undefined;
  bookEditionNo?: number | undefined;
  companyId?: number | undefined;
  isActive?: boolean | undefined;
  activeFrom?: string | undefined;
  activeTo?: string | undefined;
  page?: number | undefined;
  limit?: number | undefined;
}

export const pcmpCatalogItemSchema = z.object({
  id: z.coerce.number(),
  code: z.string(),
  name: z.string(),
});

export type PcmpCatalogItem = z.infer<typeof pcmpCatalogItemSchema>;

export const pcmpProjectPriceSchema = z.object({
  projectBookId: z.coerce.number(),
  categoryId: z.coerce.number(),
  classId: z.coerce.number(),
  bookPrice: z.number(),
  inUse: z.boolean(),
});

export type PcmpProjectPrice = z.infer<typeof pcmpProjectPriceSchema>;

export const pcmpProjectDetailSchema = z.object({
  ...pcmpProjectBaseShape,
  description: z.string().nullable(),
  updatedAt: z.coerce.date(),
  categories: z.array(pcmpCatalogItemSchema),
  classes: z.array(pcmpCatalogItemSchema),
  prices: z.array(pcmpProjectPriceSchema),
});

export type PcmpProjectDetail = z.infer<typeof pcmpProjectDetailSchema>;

export const pcmpProjectOptionsSchema = z.object({
  companies: z.array(z.object({ id: z.coerce.number(), name: z.string() })),
  bookEditions: z.array(z.object({ no: z.number(), name: z.string() })),
  categories: z.array(pcmpCatalogItemSchema),
  classes: z.array(pcmpCatalogItemSchema),
});

export type PcmpProjectOptions = z.infer<typeof pcmpProjectOptionsSchema>;

export const pcmpProjectDeleteCheckSchema = z.object({
  canDelete: z.boolean(),
  submissionCount: z.number(),
  categoryCount: z.number(),
  priceCount: z.number(),
});

export type PcmpProjectDeleteCheck = z.infer<typeof pcmpProjectDeleteCheckSchema>;

export const pcmpProjectRefSchema = z.object({
  id: z.coerce.number(),
  displayName: z.string(),
});

export type PcmpProjectRef = z.infer<typeof pcmpProjectRefSchema>;

export interface PcmpProjectPricePayload {
  categoryId: number;
  classId: number;
  bookPrice: number;
}

export interface PcmpProjectPayload {
  name: string;
  code: string;
  description: string | null;
  companyId: number;
  bookEditionNo: number;
  activeFrom: string;
  activeTo: string;
  slNo: number | null;
  isActive: boolean;
  isWebVisible: boolean;
  isMobileVisible: boolean;
  prices: PcmpProjectPricePayload[];
}
